import { useState, useEffect, useRef, useCallback, type FormEvent, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@wovendays/i18n';
import { useAuth } from '@/contexts/auth';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { apiClient } from '@/lib/api-client';

const isDev = import.meta.env.DEV;

// Shared button style
const btnClass =
  'w-full rounded-2xl bg-primary-600 px-6 py-3 font-mono text-sm uppercase tracking-wide text-white transition-all hover:bg-primary-700 hover:-translate-y-px hover:shadow-elevated-hover active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none';

export function RegisterPage() {
  const { register } = useAuth();
  const { t } = useTranslation('auth');
  const [searchParams, setSearchParams] = useSearchParams();

  // Shared form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Multi-step state (production/staging only)
  const [step, setStep] = useState<'email' | 'code' | 'passkey'>('email');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle magic link token from URL (?verify=<token>)
  useEffect(() => {
    if (isDev) return;
    const token = searchParams.get('verify');
    if (!token) return;

    setIsSubmitting(true);
    setSubmitError(null);
    apiClient
      .post<{ verified: boolean; email: string; name: string }>('/api/auth/verify-email/confirm', { token })
      .then((data) => {
        setEmail(data.email);
        setName(data.name);
        setStep('passkey');
        // Clean up URL
        setSearchParams({}, { replace: true });
      })
      .catch((err) => {
        setSubmitError(err instanceof Error ? err.message : t('register.failed'));
        setSearchParams({}, { replace: true });
      })
      .finally(() => setIsSubmitting(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  function validate(): boolean {
    const newErrors: { name?: string; email?: string } = {};
    if (!name.trim()) newErrors.name = t('nameRequired');
    if (!email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('emailInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // --- Step 1: Email + name → send verification (production) or straight to WebAuthn (dev) ---
  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      if (isDev) {
        // Dev mode: skip verification, go straight to WebAuthn
        await register(email, name);
      } else {
        // Production: send verification email
        await apiClient.post('/api/auth/verify-email', { email, name });
        setStep('code');
        setResendCountdown(60);
        // Focus first digit input after transition
        setTimeout(() => digitRefs.current[0]?.focus(), 50);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('register.failed'));
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- Step 2: Verify code ---
  const handleCodeSubmit = useCallback(
    async (codeDigits: string[]) => {
      const code = codeDigits.join('');
      if (code.length !== 6) return;

      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const data = await apiClient.post<{ verified: boolean; email: string; name: string }>(
          '/api/auth/verify-email/confirm',
          { email, code },
        );
        setName(data.name);
        setStep('passkey');
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : t('register.invalidCode'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, t],
  );

  function handleDigitChange(index: number, value: string) {
    // Only allow single digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < 5) {
      digitRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits filled
    if (digit && index === 5 && next.every((d) => d !== '')) {
      handleCodeSubmit(next);
    }
  }

  function handleDigitKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  }

  function handleDigitPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted.charAt(i);
    }
    setDigits(next);
    // Focus last filled or next empty
    const focusIdx = Math.min(pasted.length, 5);
    digitRefs.current[focusIdx]?.focus();
    // Auto-submit if all 6 filled
    if (next.every((d) => d !== '')) {
      handleCodeSubmit(next);
    }
  }

  async function handleResend() {
    if (resendCountdown > 0) return;
    setSubmitError(null);
    try {
      await apiClient.post('/api/auth/verify-email', { email, name });
      setResendCountdown(60);
      setDigits(['', '', '', '', '', '']);
      digitRefs.current[0]?.focus();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('register.failedToResend'));
    }
  }

  // --- Step 3: WebAuthn registration ---
  async function handlePasskeySubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await register(email, name);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('register.registrationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }

  // In dev mode, just render the original single-step form
  const effectiveStep = isDev ? 'email' : step;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="grid gap-2">
          {/* Hero tile */}
          <Tile variant="dark" className="p-8">
            <p className="font-mono text-xs uppercase tracking-widest text-primary-400 mb-3">{t('common:appName')}</p>
            <h1 className="font-serif text-3xl italic text-white">
              {effectiveStep === 'code' ? t('register.heroCode') : effectiveStep === 'passkey' ? t('register.heroPasskey') : t('register.heroEmail')}
            </h1>
          </Tile>

          {/* Form tile */}
          <Tile variant="cream" className="p-6">
            {submitError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {/* Step 1: Email + Name */}
            {effectiveStep === 'email' && (
              <form onSubmit={handleEmailSubmit} noValidate>
                <div className="mb-4">
                  <label htmlFor="name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('name')}
                  </label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('namePlaceholder')} />
                  {errors.name && <p className="mt-1.5 text-sm text-destructive">{errors.name}</p>}
                </div>

                <div className="mb-5">
                  <label htmlFor="email" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('email')}
                  </label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('emailPlaceholder')} />
                  {errors.email && <p className="mt-1.5 text-sm text-destructive">{errors.email}</p>}
                </div>

                <button type="submit" disabled={isSubmitting} className={btnClass}>
                  {isSubmitting ? (isDev ? t('register.settingUpPasskey') : t('register.sendingCode')) : isDev ? t('register.registerWithPasskey') : t('register.continue')}
                </button>
              </form>
            )}

            {/* Step 2: Verification code */}
            {effectiveStep === 'code' && (
              <div>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('register.codeSent')} <span className="font-medium text-foreground">{email}</span>
                </p>

                <div className="flex justify-center gap-2 mb-6">
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => { digitRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(i, e)}
                      onPaste={i === 0 ? handleDigitPaste : undefined}
                      className="w-12 h-14 text-center font-mono text-2xl rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600 transition-shadow"
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>

                <button
                  type="button"
                  disabled={isSubmitting || digits.some((d) => !d)}
                  onClick={() => handleCodeSubmit(digits)}
                  className={btnClass}
                >
                  {isSubmitting ? t('register.verifying') : t('register.verify')}
                </button>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCountdown > 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-default transition-colors"
                  >
                    {resendCountdown > 0 ? t('register.resendCountdown', { countdown: resendCountdown }) : t('register.resend')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setSubmitError(null);
                      setDigits(['', '', '', '', '', '']);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t('register.differentEmail')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: WebAuthn PassKey */}
            {effectiveStep === 'passkey' && (
              <form onSubmit={handlePasskeySubmit}>
                <p className="text-sm text-muted-foreground mb-5">
                  {t('register.emailVerified')}
                </p>
                <button type="submit" disabled={isSubmitting} className={btnClass}>
                  {isSubmitting ? t('register.settingUpPasskey') : t('register.registerWithPasskey')}
                </button>
              </form>
            )}
          </Tile>

          {/* Sign in link */}
          <Link to="/login">
            <Tile variant="cream" interactive className="p-4 text-center">
              <span className="text-sm text-muted-foreground">
                {t('register.alreadyHaveAccount')}{' '}
                <span className="font-mono text-xs uppercase tracking-wide text-primary-600">{t('register.signIn')}</span>
              </span>
            </Tile>
          </Link>
        </div>
      </div>
    </div>
  );
}
